import React, { useState } from 'react'
import { useEffect } from 'react'
import { Container, Row, Col, Card, Modal, Form, Button, Alert } from 'react-bootstrap'
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from 'react-redux'
import { useAuth } from '../../components/auth/useAuth'
import {
    getDashboardMe,
    getNotifications,
    getNoticeboard,
    getSentMessages,
    getThread,
    resetThread,
    markNotificationRead,
    markAllNotificationsRead,
    contactTutor,
    resetTutorMessage
} from '../../actions/usersActions'
import AxiosControl from '../../components/axios/AxiosControl'
import { toastSuccess, toastError } from '../../components/swal/toast'
const Axios = AxiosControl()

const formatDate = (d) => {
    if (!d) return ''
    return new Date(d).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })
}

const formatTime = (d) => {
    if (!d) return ''
    return new Date(d).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })
}

export default function Communications() {
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const { clientauth } = useAuth()
    const {
        dashboard,
        notifications,
        notificationsLoading,
        unreadCount,
        noticeboard,
        noticeboardLoading,
        sentMessages,
        sentLoading,
        thread,
        threadLoading,
        tutorMessageSending,
        tutorMessageError
    } = useSelector(state => state.users)

    const [activeTab, setActiveTab] = useState('notifications')
    const [filterUnreadOnly, setFilterUnreadOnly] = useState(false)
    const [expandedId, setExpandedId] = useState(null)
    const [showContactModal, setShowContactModal] = useState(false)
    const [contactForm, setContactForm] = useState({ subject: '', message: '', tutor: '', attachments: [] })
    const [uploadingFiles, setUploadingFiles] = useState(false)
    const [replyAttachments, setReplyAttachments] = useState([])
    const [uploadingReplyFiles, setUploadingReplyFiles] = useState(false)
    const [searchInbox, setSearchInbox] = useState('')
    const [searchBoard, setSearchBoard] = useState('')
    const [searchSent, setSearchSent] = useState('')
    const [activeThreadItem, setActiveThreadItem] = useState(null)
    const [replyText, setReplyText] = useState('')
    const [sendContext, setSendContext] = useState(null)

    const students = dashboard?.students || []
    const currentStudent = students[0] || null

    const tutors = []
    const seenTutors = new Set()
    ;(currentStudent?.odooEnrollments || []).forEach(enr => {
        if (enr.teacher && !seenTutors.has(enr.teacher)) {
            seenTutors.add(enr.teacher)
            tutors.push({ name: enr.teacher, group: enr.groupName || null })
        }
    })

    useEffect(() => {
        window.scrollTo({
            top: 0,
            behavior: 'instant',
        })
        dispatch(getNotifications())
        dispatch(getNoticeboard())
        dispatch(getSentMessages())
    }, [dispatch])

    useEffect(() => {
        if (!dashboard) dispatch(getDashboardMe())
    }, [dispatch, dashboard])

    const tabs = [
        { key: 'notifications', label: t('communications.notifications') },
        { key: 'noticeboard', label: t('communications.noticeboard') },
        { key: 'sent', label: t('communications.sent') },
    ]

    const matchesSearch = (n, s) => {
        if (!s.trim()) return true
        const q = s.toLowerCase()
        return (n.subject || '').toLowerCase().includes(q) ||
            (n.message || '').toLowerCase().includes(q) ||
            (n.senderName || '').toLowerCase().includes(q) ||
            (n.recipientName || '').toLowerCase().includes(q) ||
            (n.groupName || '').toLowerCase().includes(q)
    }

    const visibleNotifications = (filterUnreadOnly ? notifications.filter(n => !n.read) : notifications).filter(n => matchesSearch(n, searchInbox))

    const sentThreads = (() => {
        const map = new Map()
        sentMessages.forEach(m => {
            const key = m.threadId || m._id
            if (!map.has(key)) map.set(key, [])
            map.get(key).push(m)
        })
        return Array.from(map.entries()).map(([threadId, messages]) => ({
            threadId,
            messages,
            last: messages[0]
        }))
    })()

    const handleNotificationClick = (n) => {
        setExpandedId(expandedId === n._id ? null : n._id)
        if (!n.read) dispatch(markNotificationRead(n._id))
    }

    const handleOpenThread = (threadItem) => {
        setActiveThreadItem(threadItem)
        dispatch(getThread(threadItem.threadId))
    }

    const handleCloseThread = () => {
        setActiveThreadItem(null)
        dispatch(resetThread())
    }

    const handleContactChange = (field) => (e) => {
        setContactForm(prev => ({ ...prev, [field]: e.target.value }))
    }

    const handleFileSelect = async (e, target) => {
        const files = e.target.files
        if (!files || files.length === 0) return
        const formData = new FormData()
        Array.from(files).forEach(f => formData.append('files', f))
        if (target === 'reply') setUploadingReplyFiles(true)
        else setUploadingFiles(true)
        try {
            const { data, status } = await Axios.post('/api/upload/uploadfiles', formData, {
                headers: { auth: localStorage.getItem('jwt'), 'Content-Type': 'multipart/form-data' }
            })
            if (status === 200 && Array.isArray(data)) {
                if (target === 'reply') setReplyAttachments(prev => [...prev, ...data])
                else setContactForm(prev => ({ ...prev, attachments: [...prev.attachments, ...data] }))
            }
        } catch (error) {
            toastError(t('communications.uploadError'))
        } finally {
            if (target === 'reply') setUploadingReplyFiles(false)
            else setUploadingFiles(false)
            e.target.value = ''
        }
    }

    const handleContactSubmit = async (e) => {
        e.preventDefault()
        setSendContext('contact')
        dispatch(resetTutorMessage())
        const ok = await dispatch(contactTutor({
            subject: contactForm.subject,
            message: contactForm.message,
            tutorName: contactForm.tutor || null,
            groupName: tutors.find(tu => tu.name === contactForm.tutor)?.group || null,
            attachments: contactForm.attachments
        }))
        if (ok) {
            toastSuccess(typeof ok === 'string' ? ok : t('communications.messagesent'))
            setShowContactModal(false)
            setContactForm({ subject: '', message: '', tutor: tutors.length ? tutors[0].name : '', attachments: [] })
            dispatch(getSentMessages())
            dispatch(getNotifications())
        }
    }

    const handleReplySubmit = async (e) => {
        e.preventDefault()
        if (!replyText.trim() || !activeThreadItem) return
        setSendContext('reply')
        dispatch(resetTutorMessage())
        const ok = await dispatch(contactTutor({
            subject: activeThreadItem.last.subject,
            message: replyText,
            tutorName: activeThreadItem.last.recipientName || null,
            groupName: activeThreadItem.last.groupName || null,
            threadId: activeThreadItem.threadId,
            attachments: replyAttachments
        }))
        if (ok) {
            setReplyText('')
            setReplyAttachments([])
            dispatch(getThread(activeThreadItem.threadId))
            dispatch(getSentMessages())
            dispatch(getNotifications())
        }
    }

    const renderNotificationItem = (n, isNoticeboard = false) => (
        <div
            key={n._id}
            onClick={() => !isNoticeboard && handleNotificationClick(n)}
            className={`border rounded-4 p-3 mb-3 ${!isNoticeboard ? 'communication-item' : ''}`}
            style={{
                cursor: isNoticeboard ? 'default' : 'pointer',
                backgroundColor: n.read || isNoticeboard ? '#fff' : '#fff7ed',
                borderColor: n.read || isNoticeboard ? '#e5e7eb' : '#fdba74',
                boxShadow: n.read || isNoticeboard ? 'none' : '0 1px 3px rgba(249, 115, 22, 0.12)',
                transition: 'all 0.2s ease'
            }}
        >
            <div className='d-flex align-items-start gap-3'>
                <div className='d-flex align-items-center justify-content-center rounded-circle flex-shrink-0'
                    style={{ width: '40px', height: '40px', backgroundColor: n.read || isNoticeboard ? '#f3f4f6' : '#ffedd5' }}>
                    <img src={isNoticeboard ? "/images/home.svg" : "/images/exclamation.svg"} alt="notification" width={18}
                        style={{ filter: n.read || isNoticeboard ? 'none' : 'invert(48%) sepia(79%) saturate(2476%) hue-rotate(346deg) brightness(100%) contrast(97%)' }} />
                </div>
                <div className='flex-grow-1' style={{ minWidth: 0 }}>
                    <div className='d-flex justify-content-between align-items-center gap-2'>
                        <p className='mb-1 text-black' style={{ fontSize: '15px' }}>{n.subject}</p>
                        <span className='badge rounded-pill flex-shrink-0' style={{ fontSize: '10px', backgroundColor: n.read ? '#e5e7eb' : '#dc2626', color: n.read ? '#6b7280' : '#fff', visibility: n.read ? 'hidden' : 'visible' }}>
                            {t('communications.new')}
                        </span>
                    </div>
                    <p className={`text-muted mb-1 ${expandedId === n._id ? '' : 'communication-message-clamp'}`}
                        style={{ fontSize: '14px', whiteSpace: 'pre-line' }}>
                        {n.message}
                    </p>
                    {expandedId === n._id && n.attachments && n.attachments.length > 0 && (
                        <div className='mt-2'>
                            <p className='mb-1 fw-semibold' style={{ fontSize: '12px' }}>{t('communications.attachments')}:</p>
                            {n.attachments.map((a, idx) => (
                                <div key={idx} style={{ fontSize: '13px' }}>
                                    {a.url ? <a href={a.url} target='_blank' rel='noreferrer'>{a.name || a.url}</a> : <span>{a.name}</span>}
                                </div>
                            ))}
                        </div>
                    )}
                    <p className='mb-0 text-muted' style={{ fontSize: '12px' }}>
                        {formatDate(n.createdAt)} · {formatTime(n.createdAt)}
                        {n.senderName && ` · ${n.senderName}`}
                    </p>
                </div>
            </div>
        </div>
    )

    const renderSentItem = (threadItem) => (
        <div
            key={threadItem.threadId}
            onClick={() => handleOpenThread(threadItem)}
            className='border rounded-4 p-3 mb-3 communication-item'
            style={{ cursor: 'pointer', backgroundColor: '#fff', borderColor: '#e5e7eb' }}
        >
            <div className='d-flex align-items-start gap-3'>
                <div className='d-flex align-items-center justify-content-center rounded-circle flex-shrink-0'
                    style={{ width: '40px', height: '40px', backgroundColor: '#eff6ff' }}>
                    <i className="fa-solid fa-paper-plane text-primary"></i>
                </div>
                <div className='flex-grow-1' style={{ minWidth: 0 }}>
                    <div className='d-flex justify-content-between align-items-start gap-2'>
                        <p className='fw-semibold mb-1 text-black text-truncate' style={{ fontSize: '15px' }}>{threadItem.last.subject}</p>
                        {threadItem.messages.length > 1 && (
                            <span className='badge rounded-pill bg-primary flex-shrink-0' style={{ fontSize: '11px' }}>
                                {threadItem.messages.length} {t('communications.messages')}
                            </span>
                        )}
                    </div>
                    <p className='text-muted mb-1 communication-message-clamp' style={{ fontSize: '14px' }}>
                        {threadItem.last.message}
                    </p>
                    <p className='mb-0 text-muted' style={{ fontSize: '12px' }}>
                        {formatDate(threadItem.last.createdAt)} · {formatTime(threadItem.last.createdAt)}
                        {threadItem.last.recipientName && ` · ${threadItem.last.recipientName}`}
                    </p>
                </div>
                <i className="fa-solid fa-chevron-right text-muted flex-shrink-0 mt-2"></i>
            </div>
        </div>
    )

    return (
        <Container className='my-3 p-5'>
            <Row className='align-items-center'>
                <Col>
                    <h2 className='fw-bold text-black mb-1'>{t('communications.title')}</h2>
                    <p className='text-muted mb-0'>{t('communications.subtitle')}</p>
                </Col>
                <Col xs="auto" className='d-flex gap-2'>
                    <button onClick={() => setShowContactModal(true)}
                        className='btn border rounded-pill px-4 d-inline-flex align-items-center gap-2 fw-semibold'
                        style={{ color: '#374151', borderColor: '#d1d5db', background: '#fff' }}>
                        <i className="fa-solid fa-paper-plane"></i>
                        <span className='d-none d-md-inline'>{t('communications.contacttutors')}</span>
                    </button>
                    <button onClick={() => setActiveTab('notifications')}
                        className='btn btn-primary rounded-pill px-4 d-inline-flex align-items-center gap-2'>
                        <i className="fa-regular fa-bell"></i>
                        {unreadCount} {t('communications.unread')}
                    </button>
                </Col>
            </Row>

            <Row className='mt-4'>
                <Col>
                    <div className='d-flex gap-2 bg-white rounded-4 p-2 shadow-sm' style={{ maxWidth: 'fit-content' }}>
                        {tabs.map(tab => (
                            <button
                                key={tab.key}
                                onClick={() => setActiveTab(tab.key)}
                                className={`btn border-0 rounded-3 px-4 py-2 fw-semibold position-relative ${activeTab === tab.key ? 'bg-white shadow-sm text-black' : 'text-muted bg-transparent'}`}
                            >
                                {tab.label}
                                {tab.key === 'notifications' && unreadCount > 0 && (
                                    <span className='badge rounded-pill bg-danger ms-2' style={{ fontSize: '11px' }}>{unreadCount}</span>
                                )}
                            </button>
                        ))}
                    </div>
                </Col>
            </Row>

            {activeTab === 'notifications' && (
                <Row className='mt-4'>
                    <Col>
                        <Card className='border-0 rounded-4'>
                            <Card.Body className='p-4'>
                                <Form.Control
                                    type='text'
                                    value={searchInbox}
                                    onChange={e => setSearchInbox(e.target.value)}
                                    placeholder={t('communications.searchPlaceholder')}
                                    className='mb-3'
                                    style={{ borderRadius: '10px', maxWidth: '400px' }}
                                />
                                <div className='d-flex flex-wrap gap-2 mb-3'>
                                    <button
                                        onClick={() => setFilterUnreadOnly(false)}
                                        className={`rounded-pill invoice-filter-btn d-inline-flex align-items-center gap-1 border-0 px-3 py-1 ${!filterUnreadOnly ? 'active' : ''}`}
                                    >
                                        {t('communications.all')} ({notifications.length})
                                    </button>
                                    <button
                                        onClick={() => setFilterUnreadOnly(true)}
                                        className={`rounded-pill invoice-filter-btn d-inline-flex align-items-center gap-1 border-0 px-3 py-1 ${filterUnreadOnly ? 'active' : ''}`}
                                    >
                                        {t('communications.unreadonly')} ({unreadCount})
                                    </button>
                                    {unreadCount > 0 && (
                                        <button
                                            onClick={() => dispatch(markAllNotificationsRead())}
                                            className='btn btn-sm border-0 ms-auto d-inline-flex align-items-center gap-2 fw-semibold'
                                            style={{ color: '#dc2626' }}
                                        >
                                            <i className="fa-solid fa-check-double"></i>
                                            {t('communications.markallread')}
                                        </button>
                                    )}
                                </div>

                                {notificationsLoading ? (
                                    <>
                                        {[1, 2, 3].map(i => (
                                            <div key={i} className='border rounded-4 p-3 mb-3'>
                                                <div className='bg-light rounded-3' style={{ height: '14px', width: '50%' }} />
                                                <div className='bg-light rounded-3 mt-2' style={{ height: '12px', width: '90%' }} />
                                            </div>
                                        ))}
                                    </>
                                ) : visibleNotifications.length === 0 ? (
                                    <p className='text-muted text-center py-5 mb-0'>{t('communications.nonotifications')}</p>
                                ) : (
                                    visibleNotifications.map(n => renderNotificationItem(n))
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}

            {activeTab === 'noticeboard' && (
                <Row className='mt-4'>
                    <Col>
                        <Card className='border-0 rounded-4 p-4'>
                            <Form.Control
                                type='text'
                                value={searchBoard}
                                onChange={e => setSearchBoard(e.target.value)}
                                placeholder={t('communications.searchPlaceholder')}
                                className='mb-3'
                                style={{ borderRadius: '10px', maxWidth: '400px' }}
                            />
                            {noticeboardLoading ? (
                                <>
                                    {[1, 2].map(i => (
                                        <div key={i} className='border rounded-4 p-3 mb-3'>
                                            <div className='bg-light rounded-3' style={{ height: '14px', width: '50%' }} />
                                            <div className='bg-light rounded-3 mt-2' style={{ height: '12px', width: '90%' }} />
                                        </div>
                                    ))}
                                </>
                            ) : noticeboard.length === 0 ? (
                                <p className='text-muted text-center py-5 mb-0'>{t('communications.nonoticeboard')}</p>
                            ) : (
                                noticeboard.filter(n => matchesSearch(n, searchBoard)).map(n => renderNotificationItem(n, true))
                            )}
                        </Card>
                    </Col>
                </Row>
            )}

            {activeTab === 'sent' && (
                <Row className='mt-4'>
                    <Col>
                        <Card className='border-0 rounded-4 p-4'>
                            <Form.Control
                                type='text'
                                value={searchSent}
                                onChange={e => setSearchSent(e.target.value)}
                                placeholder={t('communications.searchPlaceholder')}
                                className='mb-3'
                                style={{ borderRadius: '10px', maxWidth: '400px' }}
                            />
                            {sentLoading ? (
                                <>
                                    {[1, 2].map(i => (
                                        <div key={i} className='border rounded-4 p-3 mb-3'>
                                            <div className='bg-light rounded-3' style={{ height: '14px', width: '50%' }} />
                                            <div className='bg-light rounded-3 mt-2' style={{ height: '12px', width: '90%' }} />
                                        </div>
                                    ))}
                                </>
                            ) : sentThreads.length === 0 ? (
                                <p className='text-muted text-center py-5 mb-0'>{t('communications.nosent')}</p>
                            ) : (
                                sentThreads.filter(th => matchesSearch(th.last, searchSent)).map(renderSentItem)
                            )}
                        </Card>
                    </Col>
                </Row>
            )}

            <Modal show={showContactModal} onHide={() => setShowContactModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title className='fw-bold' style={{ fontSize: '20px' }}>{t('communications.contacttutors')}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleContactSubmit}>
                    <Modal.Body>
                        {tutorMessageError && sendContext === 'contact' && (
                            <Alert variant='danger' dismissible onClose={() => dispatch(resetTutorMessage())}>
                                {tutorMessageError}
                            </Alert>
                        )}
                        <Form.Group className='mb-3'>
                            <Form.Label className='text-muted fw-semibold'>{t('communications.tutor')}</Form.Label>
                            {tutors.length === 0 ? (
                                <Form.Control type="text" value="" disabled placeholder={t('communications.notutors')} />
                            ) : (
                                <Form.Select
                                    value={contactForm.tutor}
                                    onChange={handleContactChange('tutor')}
                                    className='sd-input'
                                    required
                                >
                                    <option value="" disabled>{t('global.select')}</option>
                                    {tutors.map(tu => (
                                        <option key={tu.name} value={tu.name}>
                                            {tu.name}{tu.group ? ` · ${tu.group}` : ''}
                                        </option>
                                    ))}
                                </Form.Select>
                            )}
                        </Form.Group>
                        <Form.Group className='mb-3'>
                            <Form.Label className='text-muted fw-semibold'>{t('communications.subject')}</Form.Label>
                            <Form.Control
                                type="text"
                                value={contactForm.subject}
                                onChange={handleContactChange('subject')}
                                className='sd-input'
                                required
                            />
                        </Form.Group>
                        <Form.Group>
                            <Form.Label className='text-muted fw-semibold'>{t('communications.message')}</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={5}
                                value={contactForm.message}
                                onChange={handleContactChange('message')}
                                className='sd-input'
                                required
                            />
                        </Form.Group>
                        <Form.Group className='mt-3'>
                            <Form.Label className='text-muted fw-semibold'>{t('communications.attachments')}</Form.Label>
                            {contactForm.attachments.length > 0 && (
                                <div className='mb-2'>
                                    {contactForm.attachments.map((a, idx) => (
                                        <div key={idx} className='d-flex justify-content-between align-items-center p-2 mb-1 bg-light rounded-3'>
                                            <span style={{ fontSize: '13px' }}>{a.name || a.url}</span>
                                            <Button variant='link' size='sm' className='text-danger p-0'
                                                onClick={() => setContactForm(prev => ({ ...prev, attachments: prev.attachments.filter((_, i) => i !== idx) }))}>
                                                <i className='fa-solid fa-xmark'></i>
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            )}
                            <div className='d-flex align-items-center gap-2'>
                                <Form.Control
                                    type='file'
                                    multiple
                                    onChange={e => handleFileSelect(e, 'contact')}
                                    disabled={uploadingFiles}
                                    style={{ borderRadius: '10px' }}
                                />
                                {uploadingFiles && <span className='spinner-border spinner-border-sm text-secondary flex-shrink-0'></span>}
                            </div>
                            <p className='text-muted mb-0 mt-1' style={{ fontSize: '12px' }}>
                                {uploadingFiles ? t('communications.uploading') : t('communications.selectFiles')}
                            </p>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer className='border-0'>
                        <Button variant='light' onClick={() => setShowContactModal(false)} className='px-4'>
                            {t('global.cancel')}
                        </Button>
                        <Button type='submit' className='btn-primary px-4' disabled={tutorMessageSending || tutors.length === 0}>
                            {tutorMessageSending && sendContext === 'contact' ? (
                                <span className="spinner-border spinner-border-sm align-middle"></span>
                            ) : (
                                <>{t('communications.send')} <i className="fa-solid fa-paper-plane ms-1"></i></>
                            )}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            <Modal show={!!activeThreadItem} onHide={handleCloseThread} centered scrollable>
                <Modal.Header closeButton>
                    <Modal.Title className='fw-bold' style={{ fontSize: '18px' }}>
                        {activeThreadItem?.last?.subject}
                        {activeThreadItem?.last?.recipientName && (
                            <span className='d-block text-muted fw-normal mt-1' style={{ fontSize: '14px' }}>
                                {t('communications.with')}: {activeThreadItem.last.recipientName}
                            </span>
                        )}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ backgroundColor: '#f9fafb' }}>
                    {threadLoading ? (
                        <div className='d-flex justify-content-center py-5'>
                            <span className="spinner-border text-secondary"></span>
                        </div>
                    ) : (
                        <>
                            <div className='d-flex flex-column gap-3'>
                                {(thread.length > 0 ? thread : (activeThreadItem ? [activeThreadItem.last] : [])).map(msg => {
                                    const mine = String(msg.sender) === String(clientauth?.id)
                                    return (
                                        <div key={msg._id} className={`d-flex ${mine ? 'justify-content-end' : 'justify-content-start'}`}>
                                            <div className={`rounded-4 p-3 communication-bubble ${mine ? 'communication-bubble-mine' : 'communication-bubble-their'}`}>
                                                <p className='mb-1 fw-semibold' style={{ fontSize: '12px', opacity: 0.75 }}>
                                                    {msg.senderName || (mine ? t('profile.role') : '')} · {formatDate(msg.createdAt)} {formatTime(msg.createdAt)}
                                                </p>
                                                <p className='mb-0' style={{ fontSize: '14px', whiteSpace: 'pre-line' }}>{msg.message}</p>
                                                {msg.attachments && msg.attachments.length > 0 && (
                                                    <div className='mt-2 pt-2 border-top'>
                                                        {msg.attachments.map((a, idx) => (
                                                            <div key={idx} style={{ fontSize: '13px' }}>
                                                                {a.url ? <a href={a.url} target='_blank' rel='noreferrer'>{a.name || a.url}</a> : <span>{a.name}</span>}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>

                            <Form onSubmit={handleReplySubmit} className='mt-4 pt-3 border-top'>
                                {tutorMessageError && sendContext === 'reply' && (
                                    <Alert variant='danger' dismissible onClose={() => dispatch(resetTutorMessage())}>
                                        {tutorMessageError}
                                    </Alert>
                                )}
                                {replyAttachments.length > 0 && (
                                    <div className='mb-2'>
                                        {replyAttachments.map((a, idx) => (
                                            <div key={idx} className='d-flex justify-content-between align-items-center p-2 mb-1 bg-white rounded-3 border'>
                                                <span style={{ fontSize: '13px' }}>{a.name || a.url}</span>
                                                <Button variant='link' size='sm' className='text-danger p-0'
                                                    onClick={() => setReplyAttachments(replyAttachments.filter((_, i) => i !== idx))}>
                                                    <i className='fa-solid fa-xmark'></i>
                                                </Button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                <div className='d-flex align-items-center gap-2 mb-2'>
                                    <Form.Control
                                        type='file'
                                        multiple
                                        onChange={e => handleFileSelect(e, 'reply')}
                                        disabled={uploadingReplyFiles}
                                        style={{ borderRadius: '10px' }}
                                    />
                                    {uploadingReplyFiles && <span className='spinner-border spinner-border-sm text-secondary flex-shrink-0'></span>}
                                </div>
                                <div className='d-flex gap-2'>
                                    <Form.Control
                                        as="textarea"
                                        rows={2}
                                        value={replyText}
                                        onChange={(e) => setReplyText(e.target.value)}
                                        placeholder={t('communications.replyplaceholder')}
                                        className='sd-input'
                                    />
                                    <Button type='submit' className='btn-primary rounded-3 px-3 flex-shrink-0'
                                        disabled={tutorMessageSending || !replyText.trim()}>
                                        {tutorMessageSending && sendContext === 'reply' ? (
                                            <span className="spinner-border spinner-border-sm align-middle"></span>
                                        ) : (
                                            <i className="fa-solid fa-paper-plane"></i>
                                        )}
                                    </Button>
                                </div>
                            </Form>
                        </>
                    )}
                </Modal.Body>
            </Modal>
        </Container>
    )
}
